import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { ScrollArea } from "@/clientWeb/components/ui/scroll-area"

/**
 * Terms gate shown when a user opens **chat** without a tenant contact yet
 * (`contactPresenceId` not minted). Chat is OPTIONAL — the gate is **dismissible**
 * ("Maybe later" / close): declining just leaves chat inactive and never blocks
 * the rest of the app (meetings, scheduling). Accepting registers the contact.
 */
export function ContactTermsGate({
  open,
  busy,
  onAccept,
  onDismiss,
}: {
  open: boolean
  busy: boolean
  onAccept: () => void
  onDismiss: () => void
}) {
  const [checked, setChecked] = useState(false)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onDismiss() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Welcome to Chat</DialogTitle>
          <DialogDescription>
            Chat is optional. To start messaging, review and accept our terms —
            or skip for now and keep using the rest of {`inMeet`}.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-56 rounded-md border p-3 text-sm text-muted-foreground">
          <p className="mb-2">
            By continuing, you agree to use the messaging service responsibly. A
            contact profile is created for you within your organization so others
            in your workspace can reach you.
          </p>
          <p className="mb-2">
            Your name and email are used to identify you across the apps in your
            workspace. Messages are stored to deliver your conversation history.
          </p>
          <p>
            You are responsible for the content you share. Do not use the service
            for unlawful, abusive, or infringing activity.
          </p>
        </ScrollArea>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            disabled={busy}
            className="mt-0.5"
          />
          <span>I have read and agree to the Terms &amp; Conditions.</span>
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onDismiss} disabled={busy}>
            Maybe later
          </Button>
          <Button onClick={onAccept} disabled={!checked || busy}>
            {busy ? "Setting up…" : "Accept & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
